import { Measurement, MetricFilter, Query } from './query.types';
import { getMeasurementItem } from './measurements';
import { mapFilters, someFilters } from '../util/filters';
import { getRollupKey } from './query';

export function inferMirroredName(name: string) {
  if (name.includes('src') || name.includes('dst')) {
    return name.replace(/src|dst/, (match) => (match === 'src' ? 'dst' : 'src'));
  }
  if (name.includes('input') || name.includes('output')) {
    return name.replace(/input|output/, (match) => (match === 'input' ? 'output' : 'input'));
  }
  if (name.includes('in_') || name.includes('out_')) {
    return name.replace(/in_|out_/, (match) => (match === 'in_' ? 'out_' : 'in_'));
  }
  if (name.includes('in-') || name.includes('out-')) {
    return name.replace(/in-|out-/, (match) => (match === 'in-' ? 'out-' : 'in-'));
  }
  return null;
}

export function getMirroredItemName(measurement: Measurement, type: 'metric' | 'dimension', name: string) {
  const item = getMeasurementItem(measurement, type, name);
  const mirroredName = item?.Inverse || inferMirroredName(name);
  if (mirroredName && getMeasurementItem(measurement, type, mirroredName)) {
    return mirroredName;
  }
  return null;
}

export function getMirroredQuery(query: Query, measurement: Measurement): Query {
  const mirroredQuery = structuredClone(query);
  mirroredQuery.metrics = mirroredQuery.metrics.map((metric) => {
    const mirroredName = getMirroredItemName(measurement, 'metric', metric.name);
    return mirroredName ? { ...metric, name: mirroredName } : metric;
  });
  mirroredQuery.dimensions = mirroredQuery.dimensions.map((dimension) => {
    const mirroredName = getMirroredItemName(measurement, 'dimension', dimension.name);
    return mirroredName ? { ...dimension, name: mirroredName } : dimension;
  });
  mirroredQuery.window = mirroredQuery.window
    ? {
        ...mirroredQuery.window,
        fn: mirroredQuery.window.fn
          ? Object.fromEntries(
              Object.entries(mirroredQuery.window.fn).map(([key, value]) => {
                const mirroredName = getMirroredItemName(measurement, 'metric', key);
                return mirroredName ? [mirroredName, value] : [key, value];
              })
            )
          : undefined
      }
    : undefined;
  mirroredQuery.rollups = mirroredQuery.rollups.map((rollup) => {
    const mirroredName = getMirroredItemName(measurement, 'metric', rollup.metric);
    return mirroredName ? { ...rollup, metric: mirroredName } : rollup;
  });
  mirroredQuery.sort = mirroredQuery.sort?.map((sort) => {
    // find rollup in original query
    const rollup = query.rollups.find((r) => getRollupKey(r) === sort.rollup);
    if (rollup) {
      const mirroredName = getMirroredItemName(measurement, 'metric', rollup.metric);
      if (mirroredName) {
        return { ...sort, rollup: getRollupKey({ ...rollup, metric: mirroredName }) };
      }
    }
    return sort;
  });
  if (mirroredQuery.filters.dimensions) {
    mirroredQuery.filters.dimensions = mapFilters(mirroredQuery.filters.dimensions, (filter) => {
      const mirroredField = getMirroredItemName(measurement, 'dimension', filter.filterField);
      if (mirroredField) {
        return { ...filter, filterField: mirroredField };
      }
      return filter;
    });
  }
  if (mirroredQuery.filters.metrics) {
    mirroredQuery.filters.metrics = mapFilters(mirroredQuery.filters.metrics, (filter: MetricFilter) => {
      if (filter.metric) {
        const mirroredName = getMirroredItemName(measurement, 'metric', filter.metric);
        if (mirroredName) {
          return {
            ...filter,
            filterField: filter.filterField.replace(filter.metric, mirroredName),
            metric: mirroredName
          };
        }
      }
      return filter;
    });
  }
  return mirroredQuery;
}

export function canMirrorQuery(query: Query, measurement: Measurement): boolean {
  return (
    query.metrics.some((metric) => getMirroredItemName(measurement, 'metric', metric.name) !== null) ||
    query.dimensions.some((dimension) => getMirroredItemName(measurement, 'dimension', dimension.name) !== null) ||
    someFilters(
      query.filters.dimensions,
      (filter) => getMirroredItemName(measurement, 'dimension', filter.filterField) !== null
    ) ||
    someFilters(
      query.filters.metrics,
      (filter: MetricFilter) => !!filter.metric && getMirroredItemName(measurement, 'metric', filter.metric) !== null
    )
  );
}
